import CONSTANTS from '../../config/constants';

/**
 * Custom hook that provides the imperative handle API for the SessionViewer component
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.ExamSessionStore - Zustand store for exam session state
 * @param {Object} params.AppStore - Zustand store for app state
 * @param {Object} params.SettingsStore - Zustand store for settings state
 * @param {Object} params.PlayerStore - Zustand store for player state
 * @param {string} params.sessionSid - Session identifier
 * @param {React.RefObject} params.sessionRef - Ref to the session container element
 * @returns {Object} Imperative handle API object
 */
export function useSessionPublicApi({
  ExamSessionStore,
  AppStore,
  SettingsStore,
  PlayerStore,
  LayoutStore,
  sessionSid,
  sessionRef
}) {
  return {
    /**
     * End the session programmatically
     * @param {Object} params
     * @param {string} params.reasonId - The reason code for ending the session
     * @param {string} params.reasonText - Human-readable reason text
     * @param {string} [params.notes=null] - Additional notes about the session end
     * @param {string} [params.transitionToStatus] - Transition status ('reconnect' or 'cancelled')
     * @returns {Promise<{successful: boolean}>}
     */
    endSession: async ({ reasonId, reasonText, notes = null, transitionToStatus }) => {
      return await ExamSessionStore.endSession({ reasonId, reasonText, notes, transitionToStatus });
    },

    /**
     * Get the last connection change information
     * @returns {Object|null} The last connection change object containing type and changedAt timestamp
     */
    getLastConnectionChange: () => {
      return ExamSessionStore.lastConnectionChange || null;
    },

    /**
     * Get the timestamp when the test taker was redirected to their exam
     * @returns {string|null} ISO8601 timestamp when ExamInProgress step started, or null if not redirected yet
     * @example
     * const redirectedAt = sessionViewerRef.current.getRedirectedToExamAt();
     * if (redirectedAt) {
     *   console.log('Test taker redirected to exam at:', redirectedAt);
     * }
     */
    getRedirectedToExamAt: () => {
      return ExamSessionStore.redirectedToExamAt || null;
    },

    /**
     * Get the session data
     * @returns {Object} The session data object containing all session information
     * @example
     * const sessionData = sessionViewerRef.current.getSessionData();
     * console.log('Session status:', sessionData.status);
     * console.log('Session user:', sessionData.user);
     */
    getSessionData: () => {
      return ExamSessionStore.sessionData || {};
    },

    /**
     * Add an annotation to the session
     * @param {Object} params - Annotation parameters
     * @param {string} params.typeId - Type identifier for the annotation (e.g., 'hand-raised', 'flag', 'note')
     * @param {string} params.details - Details or description of the annotation
     * @param {string} params.createdById - ID of the user creating the annotation
     * @param {Object} [params.metadata] - Optional metadata for the annotation
     * @returns {Promise<Object|null>} The created annotation data or null if failed
     * @example
     * const annotation = await sessionViewerRef.current.addAnnotation({
     *   typeId: 'hand-raised',
     *   details: 'Student raised hand for assistance',
     *   createdById: 'proctor_123',
     *   metadata: { priority: 'high' }
     * });
     */
    addAnnotation: async ({ typeId, details, createdById, metadata }) => {
      return await ExamSessionStore.addAnnotation({ typeId, details, createdById, metadata });
    },

    /**
     * Update the session journey step
     * @param {Object} payload - Journey step update payload
     * @param {string} payload.step - The journey step identifier
     * @param {string} payload.status - The status of the step
     * @param {Object} [payload.metadata] - Optional metadata for the journey step
     * @returns {Promise<{successful: boolean, data: any, error?: any}>}
     * @example
     * const response = await sessionViewerRef.current.updateSessionJourney({
     *   step: 'identity-verification',
     *   status: 'completed',
     *   metadata: { timestamp: new Date().toISOString() }
     * });
     */
    updateSessionJourney: async (payload) => {
      return await ExamSessionStore.updateSessionJourney(payload);
    },

    /**
     * Invoke an action on the exam session
     * Sends an RPC call to the session participant to trigger an action.
     * Commonly used for approve/deny actions during check-in or to enable/disable toolbar buttons.
     * @param {Object} payload - The action payload
     * @param {string} payload.action - The action type (e.g., 'approve', 'deny', 'updateToolbarButtons')
     * @param {Object} [payload.data] - Additional data for the action
     * @returns {Promise<{successful: boolean, data: any, error: string|null}>}
     * @example
     * const result = await sessionViewerRef.current.invokeAction({
     *   action: 'updateToolbarButtons',
     *   enable: true,
     *   buttonNames: ['proctorChat', 'raiseHand']
     * });
     * if (result.successful) {
     *   console.log('Action invoked successfully');
     * } else {
     *   console.error('Action failed:', result.error);
     * }
     */
    invokeAction: async (payload) => {
      return await ExamSessionStore.invokeAction(payload);
    },

    /**
     * Enable one or more components with optional properties
     * @param {Object} components - Components to enable with their properties
     * @example
     * sessionViewerRef.current.enableComponents({
     *   chat: true,
     *   sessionDetails: true,
     *   settings: { theme: 'dark' }
     * })
     */
    enableComponents: (components) => {
      AppStore.updateEnabledComponents(components);
    },

    /**
     * Disable one or more components
     * @param {string[]} componentNames - Array of component names to disable
     * @example
     * sessionViewerRef.current.disableComponents(['chat', 'settings'])
     */
    disableComponents: (componentNames) => {
      const updates = {};
      componentNames.forEach(name => {
        updates[name] = false;
      });
      AppStore.updateEnabledComponents(updates);
    },

    /**
     * Clear all enabled components (set all to false)
     * @example
     * sessionViewerRef.current.clearComponents()
     */
    clearComponents: () => {
      const currentComponents = AppStore.enabledComponents;
      const cleared = {};
      Object.keys(currentComponents).forEach(key => {
        cleared[key] = false;
      });
      AppStore.updateEnabledComponents(cleared);
    },

    /**
     * Remove specific tabs from sessionDetails
     * @param {string[]} tabIds - Array of tab IDs to remove (e.g., ['checkin', 'flags'])
     * @example
     * sessionViewerRef.current.removeSessionDetailsTabs(['checkin'])
     */
    removeSessionDetailsTabs: (tabIds) => {
      const currentComponents = AppStore.enabledComponents;
      const sessionDetails = currentComponents.sessionDetails;
      
      if (!sessionDetails || typeof sessionDetails !== 'object') {
        return;
      }

      // Get current tabs array
      const currentTabs = sessionDetails.tabs || [];
      
      // Filter out the tabs to remove
      const filteredTabs = currentTabs.filter(tab => {
        // Handle both string tabs and object tabs
        const tabId = typeof tab === 'string' ? tab : tab.id;
        return !tabIds.includes(tabId);
      });

      // Update sessionDetails with filtered tabs
      AppStore.updateEnabledComponents({
        sessionDetails: {
          ...sessionDetails,
          tabs: filteredTabs
        }
      });
    },

    /**
     * Set the active tab in SessionDetails
     * @param {string} tab - Tab identifier (e.g., 'flags', 'checkin', 'chat', 'journey', 'info', 'computer', 'identity')
     * @example
     * sessionViewerRef.current.setSessionDetailsTab('checkin')
     */
    setSessionDetailsTab: (tab) => {
      AppStore.setSessionDetailsTab(tab);
    },

    /**
     * Get the currently active SessionDetails tab
     * @returns {string} Current tab identifier
     * @example
     * const currentTab = sessionViewerRef.current.getSessionDetailsTab()
     */
    getSessionDetailsTab: () => {
      return AppStore.getState().sessionDetailsTab;
    },

    /**
     * Open the session log modal
     * @example
     * sessionViewerRef.current.openSessionLog()
     */
    openSessionLog: () => {
      AppStore.openSessionLog();
    },

    /**
     * Close the session log modal
     * @example
     * sessionViewerRef.current.closeSessionLog()
     */
    closeSessionLog: () => {
      AppStore.closeSessionLog();
    },

    /**
     * Open the session diagnostics modal
     * @example
     * sessionViewerRef.current.openDiagnostics()
     */
    openDiagnostics: () => {
      AppStore.openDiagnosticsModal();
    },

    /**
     * Close the session diagnostics modal
     * @example
     * sessionViewerRef.current.closeDiagnostics()
     */
    closeDiagnostics: () => {
      AppStore.closeDiagnosticsModal();
    },

    /**
     * Open the incident report modal
     * @example
     * sessionViewerRef.current.openIncidentReportModal()
     */
    openIncidentReportModal: () => {
      AppStore.openIncidentReportModal();
    },

    /**
     * Reinitialize the session viewer
     * Reloads the session data and reconnects to the sync client if applicable.
     * Useful for refreshing the session state after connection issues or data updates.
     * @returns {Promise<void>}
     * @example
     * await sessionViewerRef.current.reinitialize();
     */
    reinitialize: async () => {
      await AppStore.reinitialize();
    },

    /**
     * Close the incident report modal
     * @example
     * sessionViewerRef.current.closeIncidentReportModal()
     */
    closeIncidentReportModal: () => {
      AppStore.closeIncidentReportModal();
    },

    /**
     * Set the incident report modal open state
     * @param {boolean} isOpen - Whether the modal should be open
     * @example
     * sessionViewerRef.current.setIncidentReportModalOpen(true)
     */
    setIncidentReportModalOpen: (isOpen) => {
      AppStore.setIncidentReportModalOpen(isOpen);
    },

    /**
     * Get the incident report modal open state
     * @returns {boolean} Whether the incident report modal is open
     * @example
     * const isOpen = sessionViewerRef.current.getIncidentReportModalOpen()
     */
    getIncidentReportModalOpen: () => {
      return AppStore.getState().isIncidentReportModalOpen;
    },

    /**
     * Get the incident report data from the session
     * @returns {Object|null} The incident report object or null if not available
     * @example
     * const report = sessionViewerRef.current.getIncidentReport()
     */
    getIncidentReport: () => {
      return ExamSessionStore.getState().incidentReport;
    },

    /**
     * Set the view mode for the session
     * 
     * PR-3412: Imperatively updates viewMode without remounting the component.
     * Used by VTC to control audio rendering based on focus state.
     * 
     * @param {string} viewMode - The view mode ('focus' or 'overview')
     * @returns {void}
     * 
     * @example
     * // Set to focus mode (enables audio)
     * sessionViewerRef.current.setViewMode('focus');
     * 
     * @example
     * // Set to overview mode (disables audio)
     * sessionViewerRef.current.setViewMode('overview');
     */
    setViewMode: (viewMode) => {
      AppStore.setViewMode(viewMode);
    },

    /**
     * Get the current view mode
     * 
     * @returns {string} The current view mode ('focus' or 'overview')
     * 
     * @example
     * const mode = sessionViewerRef.current.getViewMode();
     * console.log('Current view mode:', mode);
     */
    getViewMode: () => {
      return AppStore.getState().viewMode;
    },

    /**
     * Get list of currently enabled components and their properties
     * @returns {Object} Object with enabled components and their values
     * @example
     * const enabled = sessionViewerRef.current.getEnabledComponents()
     * // Returns: { chat: true, sessionDetails: true, settings: { theme: 'dark' } }
     */
    getEnabledComponents: () => {
      return { ...AppStore.getState().enabledComponents };
    },

    /**
     * Update magnification settings
     * @param {Object} settings - Magnification settings
     * @param {boolean} [settings.enabled] - Whether magnification is enabled
     * @param {number} [settings.level] - Magnification level
     * @param {number} [settings.lensSize] - Lens size in pixels
     * @example
     * sessionViewerRef.current.updateMagnification({ enabled: true, level: 2 })
     */
    updateMagnification: (settings) => {
      AppStore.updateMagnification(settings);
    },

    /**
     * Get current magnification settings
     * @returns {Object} Current magnification settings
     * @example
     * const mag = sessionViewerRef.current.getMagnification()
     * // Returns: { enabled: true, level: 2, lensSize: 200 }
     */
    getMagnification: () => {
      return { ...AppStore.getState().magnification };
    },

    /**
     * Move the session back to live mode
     * 
     * If the session is currently in recorded mode (watching playback), this will
     * switch it back to live mode to view the real-time stream.
     * 
     * @returns {void}
     * @example
     * // Switch to live mode
     * sessionViewerRef.current.goToLive();
     */
    goToLive: () => {
      if (PlayerStore) {
        PlayerStore.setActiveVideoSource(CONSTANTS.VIDEO_SOURCES.LIVE)
      }
    },

    /**
     * Re-attach all video/audio tracks to their DOM elements
     * 
     * This is useful after DOM manipulation (like portal moves) or when tracks
     * appear frozen. It finds all video/audio elements and forces them to reload
     * their media streams.
     * 
     * @returns {void}
     * @example
     * // After moving viewer in DOM
     * sessionViewerRef.current.reattachTracks();
     */
    reattachTracks: () => {
      // Find all video and audio elements in the session viewer
      if (sessionRef.current) {
        const videoElements = sessionRef.current.querySelectorAll('video');
        const audioElements = sessionRef.current.querySelectorAll('audio');

        // Force reload and play for all video elements
        videoElements.forEach((video, index) => {
          // Skip the HLS video element (it has height: 0px, width: 0px)
          // Calling load() on HLS breaks the blob URLs
          const isHlsVideo = video.style.height === '0px' && video.style.width === '0px';
          if (isHlsVideo) {
            return;
          }

          // Skip LiveKit video elements - they manage their own tracks
          // Calling load() on LiveKit videos detaches the tracks (this can cause the publisher to loose their tracks too...)
          // LiveKit videos have the class 'lk-participant-media-video' or are in the webrtc viewport
          const isLiveKitVideo = video.classList.contains('lk-participant-media-video') ||
            video.classList.contains('session-video-element') ||
            video.closest('.webrtc-viewport');
          if (isLiveKitVideo) {
            return;
          }

          // Store current time if playing recorded content
          const currentTime = video.currentTime;

          // Resume playback without calling load() to avoid detaching tracks
          // Note: video.load() would break HLS blob URLs and detach LiveKit tracks
        
          // Restore time and play
          if (currentTime > 0) {
            video.currentTime = currentTime;
          }

          video.play().catch(err => {
            console.warn(`[SessionViewer] Error playing video ${index}:`, err);
          });
        });

        // Force reload and play for all audio elements
        audioElements.forEach((audio, index) => {
          audio.load();
          audio.play().catch(err => {
            console.warn(`[SessionViewer] Error playing audio ${index}:`, err);
          });
        });
      } else {
        console.warn('[SessionViewer] Cannot reattach tracks - sessionRef not available');
      }
    },

    /**
     * Set the proctor room SID that this session is attached to
     * 
     * Used to track which proctor room a session belongs to, particularly useful
     * for transfer operations and multi-room scenarios.
     * 
     * @param {string|null} roomSid - The proctor room SID, or null to clear
     * @returns {void}
     * 
     * @example
     * // Set the attached room
     * sessionViewerRef.current.setAttachedToProctorRoomSid('room_abc123');
     * 
     * @example
     * // Clear the attached room
     * sessionViewerRef.current.setAttachedToProctorRoomSid(null);
     */
    setAttachedToProctorRoomSid: (roomSid) => {
      AppStore.setAttachedToProctorRoomSid(roomSid);
    },

    /**
     * Get the proctor room SID that this session is attached to
     * 
     * @returns {string|null} The proctor room SID, or null if not attached
     * 
     * @example
     * const roomSid = sessionViewerRef.current.getAttachedToProctorRoomSid();
     */
    getAttachedToProctorRoomSid: () => {
      return AppStore.getState().attachedToProctorRoomSid;
    },

    /**
     * Get all current settings
     * 
     * Returns settings in the same format as the SessionViewer settings prop.
     * 
     * @returns {Object} Current settings
     * @example
     * const settings = sessionViewerRef.current.getSettings();
     * // Returns: { audio: { input: {...}, output: {...} } }
     */
    getSettings: () => {
      return SettingsStore.getSettings();
    },

    /**
     * Update settings with partial updates
     * 
     * Merges the provided settings with existing settings. Only updates
     * the properties that are provided, leaving others unchanged.
     * Triggers the onSettingsChange callback with the changed settings.
     * 
     * @param {Object} settings - Partial settings to update
     * @param {Object} [settings.audio] - Audio settings
     * @param {Object} [settings.audio.input] - Input device { deviceId, label }
     * @param {Object} [settings.audio.output] - Output device { deviceId, label }
     * @example
     * sessionViewerRef.current.updateSettings({
     *   audio: {
     *     input: { deviceId: "default", label: "Default Microphone" }
     *   }
     * });
     */
    updateSettings: (settings) => {
      SettingsStore.updateSettings(settings);
    },

    /**
     * Cleanup method to properly disconnect and release all resources
     * Called when the session is being removed from the store
     * 
     * This method:
     * - Disconnects LiveKit room connection
     * - Removes event listeners
     * - Cleans up media tracks
     * - Resets store state
     * 
     * @returns {void}
     * 
     * @example
     * sessionViewerRef.current.cleanup();
     */
    cleanup: () => {
      console.log(`[SessionViewer] Cleaning up session ${sessionSid}`);

      try {
        // Disconnect LiveKit room if it exists
        const room = ExamSessionStore?.room;
        if (room) {
          console.log(`[SessionViewer] Disconnecting LiveKit room for session ${sessionSid}`);
          room.disconnect();
        }

        // Disconnection from the sync client
        const syncClient = ExamSessionStore?.sync?.client;
        if (syncClient) {
          console.log(`[SessionViewer] Disconnecting sync client for session ${sessionSid}`);
          syncClient.shutdown();
        }

        // Note: We don't manipulate DOM elements here because:
        // 1. React will handle unmounting and cleanup automatically
        // 2. Manual DOM manipulation conflicts with React's reconciliation
        // 3. This cleanup is called AFTER React has already started unmounting
        // The LiveKit and sync disconnections above are the important cleanup

        console.log(`[SessionViewer] Cleanup complete for session ${sessionSid}`);
      } catch (error) {
        console.error(`[SessionViewer] Error during cleanup for session ${sessionSid}:`, error);
      }
    },

    /**
     * Get the current session journey
     * 
     * Returns the complete journey object containing all journey steps and metadata.
     * 
     * @returns {Object|null} The journey object or null if not available
     * 
     * @example
     * const journey = sessionViewerRef.current.getJourney();
     * console.log('Current journey:', journey);
     */
    getJourney: () => {
      return ExamSessionStore.getState().journey || null;
    },

    /**
     * Get the current journey step
     * 
     * Returns the current active journey step that is in progress (not completed).
     * This is a standard step type with no completed time.
     * 
     * @returns {Object|null} The current journey step object or null if not found
     * 
     * @example
     * const currentStep = sessionViewerRef.current.getCurrentJourneyStep();
     * if (currentStep) {
     *   console.log('Current step:', currentStep.id);
     * }
     */
    getCurrentJourneyStep: () => {
      return ExamSessionStore.getCurrentJourneyStep();
    },

    /**
     * Get the most recently completed step for a given step ID
     * 
     * Returns the most recently completed journey step that matches the provided stepId.
     * Only returns steps that have been completed (have a completedTime).
     * If multiple instances of the step exist, returns the one with the most recent completedTime.
     * 
     * @param {string} stepId - The step ID to search for
     * @returns {Object|null} The most recent completed step object or null if not found
     * 
     * @example
     * const recentStep = sessionViewerRef.current.getMostRecentStep('IdentityVerification');
     * if (recentStep) {
     *   console.log('Last completed at:', recentStep.completedTime);
     *   console.log('Step data:', recentStep);
     * }
     */
    getMostRecentStep: (stepId) => {
      return ExamSessionStore.getMostRecentStep(stepId);
    },

    /**
     * Get unfinished journey step IDs
     * 
     * Returns an array of step IDs for all journey steps that have not been completed yet
     * (steps with no completedDate).
     * 
     * @returns {string[]} Array of unfinished step IDs
     * 
     * @example
     * const unfinishedStepIds = sessionViewerRef.current.getUnfinishedJourneyStepIds();
     * console.log('Unfinished steps:', unfinishedStepIds);
     * // Returns: ['WaitingForProctor', 'IdentityVerification']
     * 
     * // Check if specific step is unfinished
     * if (unfinishedStepIds.includes('WaitingForProctor')) {
     *   console.log('User is still waiting for proctor');
     * }
     */
    getUnfinishedJourneyStepIds: () => {
      return ExamSessionStore.getUnfinishedJourneyStepIds();
    },

    /**
     * Send the test taker to specific onboarding steps
     * 
     * Adds one or more steps to the session journey. Only adds steps that are not already
     * in the unfinished steps list. Optionally unfocuses the session and adds WaitingForProctor step.
     * 
     * @param {Object} params - Parameters for sending to steps
     * @param {string[]} params.stepIds - Array of step IDs to add to the journey
     * @param {boolean} [params.unfocusSession=false] - Whether to unfocus the session and add WaitingForProctor step
     * @param {boolean} [params.pushUserToNextStep=true] - Whether to push the user to the next step
     * @param {boolean} [params.completeCurrentStep=false] - Whether to complete the current step before adding new ones
     * @returns {Promise<{successful: boolean, data: any, error?: any}>}
     * 
     * @example
     * // Send user back to identity verification
     * const result = await sessionViewerRef.current.sendToStep({
     *   stepIds: ['IdentityVerification'],
     *   unfocusSession: true,
     *   pushUserToNextStep: true,
     *   completeCurrentStep: false
     * });
     * 
     * @example
     * // Add WaitingForProctor step without unfocusing
     * const result = await sessionViewerRef.current.sendToStep({
     *   stepIds: ['WaitingForProctor'],
     *   unfocusSession: false,
     *   pushUserToNextStep: false,
     *   completeCurrentStep: false
     * });
     */
    sendToStep: async ({ stepIds = [], unfocusSession = false, pushUserToNextStep = true, completeCurrentStep = false }) => {
      return await ExamSessionStore.sendToStep({ stepIds, unfocusSession, pushUserToNextStep, completeCurrentStep });
    },

    /**
     * Set the mute state for the session audio
     * 
     * When muted, this will:
     * - Mute the HLS video playback
     * - Unsubscribe from all remote audio tracks in the LiveKit room (saves bandwidth)
     * 
     * When unmuted, this will:
     * - Unmute the HLS video playback
     * - Subscribe to all remote audio tracks in the LiveKit room
     * 
     * @param {boolean} muted - Whether to mute the audio
     * @returns {void}
     * 
     * @example
     * // Mute the session audio
     * sessionViewerRef.current.setMuted(true);
     * 
     * @example
     * // Unmute the session audio
     * sessionViewerRef.current.setMuted(false);
     */
    setMuted: (muted) => {
      if (PlayerStore) {
        PlayerStore.setMuted(muted);
      }
    },

    /**
     * Get the current mute state
     * 
     * @returns {boolean} Whether the audio is currently muted
     * 
     * @example
     * const isMuted = sessionViewerRef.current.getMuted();
     * console.log('Audio is muted:', isMuted);
     */
    getMuted: () => {
      return PlayerStore ? PlayerStore.getState().muted : false;
    },

    /**
     * Toggle the mute state
     * 
     * If currently muted, this will unmute. If currently unmuted, this will mute.
     * 
     * @returns {void}
     * 
     * @example
     * // Toggle mute state
     * sessionViewerRef.current.toggleMuted();
     */
    toggleMuted: () => {
      if (PlayerStore) {
        const currentMuted = PlayerStore.getState().muted;
        PlayerStore.setMuted(!currentMuted);
      }
    },

    /**
     * Unsubscribe from all remote audio tracks
     * 
     * This will unsubscribe from all audio tracks in the LiveKit room without
     * affecting the mute state of the video player. Useful for reducing bandwidth
     * when you don't need to hear the audio but want to keep the video unmuted.
     * 
     * @returns {void}
     * 
     * @example
     * // Unsubscribe from all audio tracks
     * sessionViewerRef.current.unsubscribeFromAudioTracks();
     */
    unsubscribeFromAudioTracks: () => {
      const room = AppStore?.getState()?.room;
      
      if (room && room.remoteParticipants) {
        room.remoteParticipants.forEach((participant) => {
          participant.audioTrackPublications.forEach((publication) => {
            if (publication.isSubscribed) {
              publication.setSubscribed(false);
            }
          });
        });
      }
    },

    /**
     * Subscribe to all remote audio tracks
     * 
     * This will subscribe to all audio tracks in the LiveKit room without
     * affecting the mute state of the video player.
     * 
     * @returns {void}
     * 
     * @example
     * // Subscribe to all audio tracks
     * sessionViewerRef.current.subscribeToAudioTracks();
     */
    subscribeToAudioTracks: () => {
      const room = AppStore?.getState()?.room;
      
      if (room && room.remoteParticipants) {
        room.remoteParticipants.forEach((participant) => {
          participant.audioTrackPublications.forEach((publication) => {
            if (!publication.isSubscribed) {
              publication.setSubscribed(true);
            }
          });
        });
      }
    },

    /**
     * Expand the SessionDetails pane
     * 
     * Programmatically expands the SessionDetails pane if it is currently collapsed.
     * This is useful when you want to ensure the details pane is visible.
     * 
     * @returns {void}
     * 
     * @example
     * // Expand the SessionDetails pane
     * sessionViewerRef.current.expandSessionDetails();
     */
    expandSessionDetails: () => {
      if (LayoutStore?.detailsSplitView?.isCollapsed) {
        LayoutStore.detailsSplitView.expand();
      }
    },

    /**
     * Collapse the SessionDetails pane
     * 
     * Programmatically collapses the SessionDetails pane if it is currently expanded.
     * This is useful when you want to maximize the video player area.
     * 
     * @returns {void}
     * 
     * @example
     * // Collapse the SessionDetails pane
     * sessionViewerRef.current.collapseSessionDetails();
     */
    collapseSessionDetails: () => {
      if (LayoutStore?.detailsSplitView && !LayoutStore.detailsSplitView.isCollapsed) {
        LayoutStore.detailsSplitView.collapse();
      }
    },

    /**
     * Add an overlay to the overlay stack
     * 
     * Adds a named overlay to the overlay stack. The overlay can be added to the top
     * (default) or bottom of the stack. The current overlay is always the first item
     * in the stack (index 0 = top).
     * 
     * @param {Object} params - Overlay parameters
     * @param {string} params.name - Name/identifier of the overlay to add
     * @param {boolean} [params.last=false] - If true, adds overlay to bottom of stack; if false, adds to top
     * @returns {void}
     * 
     * @example
     * // Add overlay to top of stack (becomes current overlay)
     * sessionViewerRef.current.setOverlay({ name: 'session-removed' });
     * 
     * @example
     * // Add overlay to bottom of stack (queued for later)
     * sessionViewerRef.current.setOverlay({ name: 'connection-warning', last: true });
     */
    setOverlay: ({ name, last = false }) => {
      ExamSessionStore.setOverlay({ name, last });
    },

    /**
     * Disconnect from the LiveKit room
     * 
     * Disconnects the SessionViewer from the LiveKit room, closing all media connections.
     * This is useful when you want to manually disconnect from the session without
     * cleaning up the entire component.
     * 
     * @returns {void}
     * 
     * @example
     * // Disconnect from the LiveKit room
     * sessionViewerRef.current.disconnect();
     */
    disconnect: () => {
      const room = AppStore?.getState()?.room;
      if (room) {
        console.log(`[SessionViewer] Disconnecting LiveKit room for session ${sessionSid}`);
        room.disconnect();
      } else {
        console.warn(`[SessionViewer] No LiveKit room found to disconnect for session ${sessionSid}`);
      }
    },

    /**
     * Get SessionViewer constants
     * 
     * Returns the CONSTANTS object containing all SessionViewer configuration values
     * including localStorage keys, video sources, recording statuses, and LiveKit settings.
     * 
     * @returns {Object} The CONSTANTS object
     * 
     * @example
     * const constants = sessionViewerRef.current.getConstants();
     * console.log('Video sources:', constants.VIDEO_SOURCES);
     * // Returns: { LIVE: 'live', RECORDED: 'recorded' }
     * 
     * @example
     * // Use constants for overlay names or configuration
     * const constants = sessionViewerRef.current.getConstants();
     * if (constants.RECORDING_STATUS.COMPLETE === 'complete') {
     *   // Handle complete recording
     * }
     */
    getConstants: () => {
      return CONSTANTS;
    },

    /**
     * Update a user interaction in the sync document
     * 
     * Updates or creates a user interaction entry in the sync document under the userInteractions key.
     * The userInteractions object is keyed by user key SID. This function also automatically adds
     * an updatedAt timestamp to track when the interaction was last modified.
     * 
     * @async
     * @function updateUserInteraction
     * @param {string} userKeySid - The SID of the user key to update
     * @param {Object} interactionData - The interaction data object to store
     * @returns {Promise<{successful: boolean, error?: string}>}
     * 
     * @example
     * // Track a button click interaction
     * const result = await sessionViewerRef.current.updateUserInteraction('userKey_123', {
     *   lastInteraction: new Date().toISOString(),
     *   interactionType: 'click',
     *   metadata: { button: 'submit', page: 'exam' }
     * });
     * 
     * @example
     * // Track a page view
     * await sessionViewerRef.current.updateUserInteraction('userKey_456', {
     *   interactionType: 'pageView',
     *   page: '/exam/question/5',
     *   timestamp: new Date().toISOString()
     * });
     */
    updateUserInteraction: async (userKeySid, interactionData) => {
      return await ExamSessionStore.updateUserInteraction(userKeySid, interactionData);
    },

    /**
     * Get a user interaction from the sync document
     * 
     * Retrieves a specific user interaction entry from the sync document.
     * Returns null if the interaction does not exist.
     * 
     * @function getUserInteraction
     * @param {string} userKeySid - The SID of the user key to retrieve
     * @returns {Object|null} The user interaction object or null if not found
     * 
     * @example
     * const interaction = sessionViewerRef.current.getUserInteraction('userKey_123');
     * if (interaction) {
     *   console.log('Last interaction:', interaction.lastInteraction);
     *   console.log('Interaction type:', interaction.interactionType);
     *   console.log('Updated at:', interaction.updatedAt);
     * } else {
     *   console.log('No interaction found for this user key');
     * }
     */
    getUserInteractions: (userKeySid) => {
      return ExamSessionStore.getUserInteractions(userKeySid);
    },

    /**
     * Get all user interactions from the sync document
     * 
     * Retrieves all user interaction entries from the sync document.
     * Returns an object keyed by userKeySid with interaction data as values.
     * Returns empty object if no interactions exist.
     * 
     * @function getAllUserInteractions
     * @returns {Object} Object containing all user interactions keyed by userKeySid
     * 
     * @example
     * const allInteractions = sessionViewerRef.current.getAllUserInteractions();
     * console.log('All interactions:', allInteractions);
     * // Output: { 'userKey_123': { isFocused: true, ... }, 'userKey_456': { isTalking: true, ... } }
     * 
     * @example
     * // Iterate through all user interactions
     * const interactions = sessionViewerRef.current.getAllUserInteractions();
     * Object.entries(interactions).forEach(([userKeySid, interaction]) => {
     *   console.log(`User ${userKeySid}:`, interaction.interactionType);
     * });
     * 
     * @example
     * // Count focused proctors
     * const interactions = sessionViewerRef.current.getAllUserInteractions();
     * const focusedCount = Object.values(interactions).filter(
     *   i => i.type === 'proctor' && i.isFocused === true
     * ).length;
     * console.log('Focused proctors:', focusedCount);
     */
    getAllUserInteractions: () => {
      return ExamSessionStore.getAllUserInteractions();
    },

    /**
     * Remove a user interaction from the sync document
     * 
     * Removes a specific user interaction entry from the sync document.
     * This permanently deletes the interaction data for the specified user key.
     * 
     * @async
     * @function removeUserInteraction
     * @param {string} userKeySid - The SID of the user key to remove
     * @returns {Promise<{successful: boolean, error?: string}>}
     * 
     * @example
     * // Remove a user interaction
     * const result = await sessionViewerRef.current.removeUserInteraction('userKey_123');
     * if (result.successful) {
     *   console.log('User interaction removed successfully');
     * } else {
     *   console.error('Failed to remove interaction:', result.error);
     * }
     */
    removeUserInteraction: async (userKeySid) => {
      return await ExamSessionStore.removeUserInteraction(userKeySid);
    }
  };
}
